import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages a product info drawer with tabbed content.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement[]} tabButtons - The tab button elements.
 * @property {HTMLDivElement[]} tabPanels - The tab panel elements.
 *
 * @extends {DialogComponent}
 */
class ProductInfoDrawerComponent extends DialogComponent {
  /** @type {number} */
  #currentTabIndex = 0;

  #onResize = () => this.#updateTabIndicator(false);

  connectedCallback() {
    super.connectedCallback();
    this.#activateTab(0);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
  }

  /**
   * Opens the drawer to the first tab.
   */
  open() {
    this.showDialog();
    // The dialog had no layout while closed — position the indicator now,
    // without animating it into place.
    requestAnimationFrame(() => this.#updateTabIndicator(false));
  }

  /**
   * Opens the drawer and activates a specific tab by index.
   *
   * @param {string} index - The tab index to open to.
   */
  openTab(index) {
    this.#activateTab(Number(index));
    this.showDialog();
    requestAnimationFrame(() => this.#updateTabIndicator(false));
  }

  close() {
    this.closeDialog();
  }

  /**
   * Finds the size chart modal on the page and opens it.
   */
  openSizeChart() {
    const sizeChartModal = document.querySelector('dialog-component.size-chart-modal');
    if (sizeChartModal && typeof sizeChartModal.showDialog === 'function') {
      sizeChartModal.showDialog();
    }
  }

  /**
   * Switches to the tab at the given index (used inside the drawer).
   *
   * @param {string} index - The tab index to switch to.
   */
  switchTab(index) {
    this.#activateTab(Number(index));
  }

  /**
   * Handles keyboard navigation on the tab bar.
   *
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const { tabButtons } = this.refs;
    if (!tabButtons?.length) return;

    const currentIndex = tabButtons.findIndex((btn) => btn === document.activeElement);
    let newIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabButtons.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex < tabButtons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabButtons.length - 1;
        break;
      default:
        return;
    }

    tabButtons[newIndex]?.focus();
    this.#activateTab(newIndex);
  }

  /**
   * Activates a tab by index.
   *
   * @param {number} index
   */
  #activateTab(index) {
    const { tabButtons, tabPanels } = this.refs;
    if (!tabButtons?.length || !tabPanels?.length) return;

    tabButtons.forEach((btn, i) => {
      const isActive = i === index;
      btn.setAttribute('aria-selected', String(isActive));
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    tabPanels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== index);
    });

    this.#currentTabIndex = index;
    this.#updateTabIndicator();
  }

  /**
   * Moves the sliding active-tab indicator (the tab bar's ::after) to the
   * current tab by updating the CSS custom properties it is positioned with.
   *
   * @param {boolean} [animate] - Pass false to reposition without sliding.
   */
  #updateTabIndicator(animate = true) {
    const { tabButtons } = this.refs;
    const button = tabButtons?.[this.#currentTabIndex];
    const tablist = button?.parentElement;
    if (!button || !tablist) return;

    if (!animate) tablist.style.setProperty('--tab-indicator-speed', '0s');

    this.style.setProperty('--tab-indicator-left', `${button.offsetLeft}px`);
    this.style.setProperty('--tab-indicator-width', `${button.offsetWidth}px`);

    if (!animate) {
      requestAnimationFrame(() => tablist.style.removeProperty('--tab-indicator-speed'));
    }
  }
}

if (!customElements.get('product-info-drawer-component')) {
  customElements.define('product-info-drawer-component', ProductInfoDrawerComponent);
}
